/**
 * The contacts still SAVING on one unit (fast save), for the "Saving contact..." rows of the unit's contact list.
 *
 * Backed by `fieldService_listPendingGeneratorContacts`, which reads the server-only pending records. A person being saved is NOT in
 * `appsheet_shared_contacts` and NOT on the unit's Contacts cell until AppSheet has issued the real Contact ID, so this is the only
 * place they appear. While any row is saving the query polls; the moment the list empties it refreshes the caches that show the real
 * contact, so the greyed row is replaced by the real one without a manual refresh.
 *
 * @module hooks/fieldService/usePendingGeneratorContacts
 */
import { useEffect, useRef } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { httpsCallable } from '../../lib/firebase/authReadyCallable';
import { functions } from '../../config/firebase';
import { useOrganization } from '../../contexts/OrganizationContext';
import { dataHubContactKeys } from '../useDataHubContacts';
import { dataHubEquipmentKeys } from '../useDataHubEquipment';
import { generatorKeys } from '../useGenerator';
import { sharedContactKeys } from './useSharedContactMutations';

/** One person still saving. Names and a role only: no phone or email is sent for this row. */
export interface PendingGeneratorContact {
  pendingId: string;
  /** attach = a person being saved; detach = a person being REMOVED from the unit (L-0889). Absent from an older server = attach. */
  op?: 'attach' | 'detach';
  /** detach only: the contact being removed, so the unit's normal row for them can be hidden while the removal is on its way. */
  contactId?: string;
  mode: 'new' | 'existing';
  display: { firstName: string; lastName: string; company: string; role: string };
  /** sending = normal; stale = taking longer than expected; needs-attention = a person must look. */
  state: 'sending' | 'stale' | 'needs-attention';
  attentionKind?: 'create' | 'link';
  /** AppSheet provably did not create the person: they can simply be added again. */
  failedClean?: boolean;
  reason?: string;
  createdAt: string;
}

export const pendingContactKeys = {
  all: ['pending-generator-contacts'] as const,
  forGenerator: (generatorId: string | undefined, orgId: string | undefined) =>
    [...pendingContactKeys.all, generatorId, orgId] as const,
};

/** How often the list is re-read while something is saving normally. */
export const PENDING_POLL_MS = 4000;
/** How often once every saving row has gone stale (taking longer than usual): slow down instead of hammering the server. */
export const PENDING_STALE_POLL_MS = 30_000;

/**
 * Contact ids whose normal row must be HIDDEN: their removal is on its way to AppSheet and ON TIME ('sending'). Once it is taking longer than
 * usual ('stale': the queue or the connection may be off and no pass is running) or needs a person, the row is SHOWN again with the note
 * beside it, so the person can still act on the contact. The link may still exist in AppSheet.
 */
export function removingContactIds(rows: readonly PendingGeneratorContact[] | undefined): Set<string> {
  const out = new Set<string>();
  for (const r of rows ?? []) {
    if (r.op === 'detach' && r.contactId && r.state === 'sending') out.add(r.contactId.trim().toLowerCase());
  }
  return out;
}

/** Rows that still need the poll: a flagged row waits for a person, not for the queue. */
export function hasRowsStillSaving(rows: readonly PendingGeneratorContact[] | undefined): boolean {
  return !!rows && rows.some((r) => r.state !== 'needs-attention');
}

/** The next poll delay: none when nothing is saving, fast while something is on time, slow when everything saving is already stale. */
export function pollIntervalFor(rows: readonly PendingGeneratorContact[] | undefined): number | false {
  if (!hasRowsStillSaving(rows)) return false;
  return rows!.some((r) => r.state === 'sending') ? PENDING_POLL_MS : PENDING_STALE_POLL_MS;
}

/**
 * @param generatorId Business Generator ID of the unit (undefined disables the query).
 * @return The unit's pending contacts (empty when none).
 */
export function usePendingGeneratorContacts(generatorId: string | undefined) {
  const { currentOrganization } = useOrganization();
  const orgId = currentOrganization?.organizationId;
  const queryClient = useQueryClient();

  const query = useQuery({
    queryKey: pendingContactKeys.forGenerator(generatorId, orgId),
    queryFn: async (): Promise<PendingGeneratorContact[]> => {
      const fn = httpsCallable<{ generatorId: string }, { pending: PendingGeneratorContact[] }>(
        functions,
        'fieldService_listPendingGeneratorContacts'
      );
      const { data } = await fn({ generatorId: generatorId as string });
      return data.pending ?? [];
    },
    enabled: !!generatorId && !!orgId,
    staleTime: 0,
    refetchInterval: (q) => pollIntervalFor(q.state.data),
  });

  // When rows that were saving disappear, the real contact is on the unit now: refresh everything that shows it.
  const hadRows = useRef(false);
  const rows = query.data;
  useEffect(() => {
    if (!rows) return;
    if (rows.length > 0) {
      hadRows.current = true;
      return;
    }
    if (hadRows.current) {
      hadRows.current = false;
      void queryClient.invalidateQueries({ queryKey: generatorKeys.all });
      void queryClient.invalidateQueries({ queryKey: dataHubEquipmentKeys.all });
      void queryClient.invalidateQueries({ queryKey: sharedContactKeys.all });
      void queryClient.invalidateQueries({ queryKey: dataHubContactKeys.all });
    }
  }, [rows, queryClient]);

  return query;
}
